import React, { useState } from 'react'
import GoogleMap from './GoogleMap'
import styled from 'styled-components'
import { circleToRing } from './circleGeofence'
const googleMapURL = `https://maps.googleapis.com/maps/api/js?libraries=geometry&key=${process.env.REACT_APP_GOOGLE_MAP_WEBSITE_KEY}`

function GeofenceMapView(props) {
  const { branch: { delivery: { geofence: { coordinates = [] } = {} } = {}, coord_lat = 52.561928, coord_long = -1.464854 } } = props
  const existingPaths = (coordinates[0] || []).map(latLng => ({ lng: latLng[0], lat: latLng[1] })).slice(0, -1)
  const hasExistingFence = existingPaths.length > 0

  const [mode, setMode] = useState(hasExistingFence ? 'view' : 'shape')
  const [drawnPath, setDrawnPath] = useState([])
  const [pin, setPin] = useState({ lat: Number(coord_lat), lng: Number(coord_long) })
  const [radiusMiles, setRadiusMiles] = useState('')
  const drawing = mode === 'shape'
  const radiusMode = mode === 'radius'

  const miles = Number(radiusMiles)
  const hasValidRadius = Number.isFinite(miles) && miles > 0
  const radiusPreview = radiusMode && hasValidRadius
    ? circleToRing(pin, miles, 64).map(([lng, lat]) => ({ lat, lng }))
    : []

  const persistGeofence = async (ring, editing, center) => {
    const coords = [...ring]
    coords.push(coords[0])
    const { branch, branch: { _id: ID } = {} } = props
    const delivery = { ...branch.delivery, geofence: { type: 'Polygon', coordinates: [coords] } }
    const centerCoords = center ? { coord_lat: center.lat.toString(), coord_long: center.lng.toString() } : {}
    try {
      await props.updateBranch(ID, { ...branch, ...centerCoords, delivery })
      if (!editing) {
        window.location.reload()
        return
      }
      await props.getBranch(ID)
    } catch (error) {
      console.error('Failed to save geofence', error)
    }
  }

  const onPolygonEdited = polygon => {
    const path = polygon.getPath()
    const ring = []
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i)
      ring.push([point.lng(), point.lat()])
    }
    persistGeofence(ring, true)
  }

  const addVertex = latLng => setDrawnPath(prev => [...prev, { lat: latLng.lat(), lng: latLng.lng() }])

  const saveDrawnFence = () => {
    if (drawnPath.length < 3) return
    persistGeofence(drawnPath.map(p => [p.lng, p.lat]), false)
  }

  const saveRadiusFence = () => {
    if (!hasValidRadius) return
    persistGeofence(circleToRing(pin, miles, 64), false, pin)
  }

  const startShape = () => { setDrawnPath([]); setMode('shape') }
  const startRadius = () => { setRadiusMiles(''); setMode('radius') }
  const cancel = () => { setDrawnPath([]); setMode('view') }

  const onMarkerDragEnd = async coord => {
    const { latLng } = coord
    setPin({ lat: latLng.lat(), lng: latLng.lng() })
    // In radius mode the centre is only committed when the geofence is saved,
    // so dragging then cancelling leaves the branch coordinates untouched.
    if (radiusMode) return
    const { branch, branch: { _id: ID } = {} } = props
    await props.updateBranch(ID, { ...branch, coord_lat: latLng.lat().toString(), coord_long: latLng.lng().toString() })
  }

  return (
    <Container>
      <GoogleMap
        googleMapURL={googleMapURL}
        loadingElement={<p>Loading branch...</p>}
        containerElement={<Container />}
        mapElement={<div style={gStyle} />}
        center={pin}
        drawing={drawing}
        radiusMode={radiusMode}
        drawnPath={drawnPath}
        previewPath={radiusPreview}
        onMapClick={addVertex}
        onPolygonEdited={onPolygonEdited}
        onMarkerDragEnd={onMarkerDragEnd}
        paths={drawing || radiusMode ? [] : existingPaths} />
      <Controls>
        {mode === 'view' && (
          <>
            <Button type="button" onClick={startShape}>Redraw shape</Button>
            <Button type="button" onClick={startRadius}>Use radius</Button>
          </>
        )}
        {drawing && (
          <>
            <Hint>Click on the map to add points, then save.</Hint>
            <Button type="button" onClick={() => setDrawnPath(prev => prev.slice(0, -1))} disabled={!drawnPath.length}>Undo</Button>
            <Button type="button" onClick={() => setDrawnPath([])} disabled={!drawnPath.length}>Clear</Button>
            <Button type="button" primary onClick={saveDrawnFence} disabled={drawnPath.length < 3}>Save geofence</Button>
            <Button type="button" onClick={startRadius}>Use radius</Button>
            {hasExistingFence && <Button type="button" onClick={cancel}>Cancel</Button>}
          </>
        )}
        {radiusMode && (
          <>
            <Hint>Drag the pin to position it, set the radius, then save.</Hint>
            <MilesInput type="number" min="0" step="0.1" value={radiusMiles} placeholder="Miles"
              onChange={e => setRadiusMiles(e.target.value)} />
            <Button type="button" primary onClick={saveRadiusFence} disabled={!hasValidRadius}>Save radius geofence</Button>
            <Button type="button" onClick={startShape}>Draw shape instead</Button>
            {hasExistingFence && <Button type="button" onClick={cancel}>Cancel</Button>}
          </>
        )}
      </Controls>
    </Container>
  )
}

function GeofenceGoogleMap(props) {
  const { branch: { _id: branchId } = {} } = props
  return <GeofenceMapView key={branchId} {...props} />
}

export default GeofenceGoogleMap

const gStyle = {
  width: '100%',
  height: '325px',
  borderRadius: '13px'
}

const Container = styled.div`
&&
{
  position: relative;
}
`

const Controls = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  z-index: 1;
`

const Hint = styled.span`
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #333;
`

const MilesInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  cursor: pointer;
  color: ${p => (p.primary ? '#fff' : '#2f9d6d')};
  background: ${p => (p.primary ? '#2f9d6d' : '#fff')};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
